import React, { useState } from 'react';
import {
  Card, CardContent, Button, Typography, Box, Alert,
  LinearProgress, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import { publisherContentUploadApi, downloadTemplateApi } from '../../api/uploads';

const CONTENT_TYPES = [
  { value: 'Image', label: 'Image', extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'], accept: 'image/*' },
  { value: 'PDF', label: 'PDF', extensions: ['.pdf'], accept: '.pdf' },
  { value: 'Excel', label: 'Excel', extensions: ['.xlsx', '.xls'], accept: '.xlsx,.xls' },
  { value: 'CSV', label: 'CSV', extensions: ['.csv'], accept: '.csv' },
  { value: 'Docs', label: 'Docs (Word)', extensions: ['.doc', '.docx'], accept: '.doc,.docx' },
];

function getExtension(filename) {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx).toLowerCase() : '';
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

export default function UploadBooks() {
  const [contentType, setContentType] = useState('');
  const [file, setFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedType = CONTENT_TYPES.find((t) => t.value === contentType);

  const handleContentTypeChange = (e) => {
    setContentType(e.target.value);
    setFile(null);
    setError('');
    setSubmitted(false);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0] || null;
    setSubmitted(false);
    setError('');
    if (selected && selectedType && !selectedType.extensions.includes(getExtension(selected.name))) {
      setFile(null);
      setError(
        `Selected file does not match content type "${selectedType.label}". `
        + `Allowed extensions: ${selectedType.extensions.join(', ')}`
      );
      return;
    }
    setFile(selected);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!contentType) { setError('Please select a content type.'); return; }
    if (!file) { setError('Please select a file.'); return; }
    if (selectedType && !selectedType.extensions.includes(getExtension(file.name))) {
      setError(`Selected file does not match content type "${selectedType.label}".`);
      return;
    }
    setLoading(true); setError(''); setSubmitted(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('content_type', contentType);
      await publisherContentUploadApi(fd);
      setSubmitted(true);
      setFile(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplate = async () => {
    try {
      const res = await downloadTemplateApi();
      downloadBlob(res.data, 'book_upload_template.xlsx');
    } catch {
      // ignore
    }
  };

  return (
    <Layout title="Upload Content">
      <PageHeader
        title="Upload Book Content"
        breadcrumbs={[{ label: 'Publisher' }, { label: 'Upload Content' }]}
        action={
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleTemplate}>
            Download Excel Template
          </Button>
        }
      />

      <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Select & Submit File</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload your book data as Image, Excel, CSV, PDF, or Word document. Admin will review and publish on the portal.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {submitted && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ mb: 2 }}
              >
                Your data has been successfully submitted. It will be reviewed and published by the Admin on the dashboard soon.
              </Alert>
            )}

            <Box component="form" onSubmit={handleUpload}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="content-type-label">Content Type</InputLabel>
                <Select
                  labelId="content-type-label"
                  label="Content Type"
                  value={contentType}
                  onChange={handleContentTypeChange}
                >
                  {CONTENT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{
                border: '2px dashed', borderColor: 'divider', borderRadius: 2,
                p: 4, textAlign: 'center', mb: 2, bgcolor: 'background.default',
                opacity: contentType ? 1 : 0.5,
              }}>
                <CloudUploadIcon sx={{ fontSize: 52, color: 'primary.main', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedType
                    ? `Supported format: ${selectedType.label} (${selectedType.extensions.join(', ')})`
                    : 'Select a content type to see supported formats'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Maximum file size: 10 MB
                </Typography>
                <input
                  type="file"
                  accept={selectedType?.accept}
                  disabled={!contentType}
                  onChange={handleFileChange}
                  style={{ display: 'block', margin: '0 auto' }}
                />
                {file && (
                  <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
                    Selected: {file.name}
                  </Typography>
                )}
              </Box>

              {loading && <LinearProgress sx={{ mb: 2 }} />}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || !file || !contentType}
                startIcon={<CloudUploadIcon />}
              >
                {loading ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </Box>

          </CardContent>
        </Card>
    </Layout>
  );
}
